import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/auth';
import { CartContext } from '../../App';
import { useLanguage } from '../../contexts/LanguageContext';
import CurrencyTicker from '../../components/CurrencyTicker';

import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet';
import { 
  Search, 
  ShoppingCart, 
  User, 
  Menu, 
  ChevronDown,
  Home,
  Package,
  Info,
  Phone,
  Heart,
  LogOut,
  Settings,
  Loader2,
} from 'lucide-react';
import apiInstance from '../../utils/axios';

const StoreHeader = () => {
  const [isLoggedIn, user] = useAuthStore((state) => [state.isLoggedIn, state.user]);
  const [cartCount] = useContext(CartContext);
  const { t, currentLanguage, changeLanguage, languages } = useLanguage();

  const navigate = useNavigate();
  const location = useLocation();

  const [search, setSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const searchRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const debounceTimer = useRef(null);

  // Simple fuzzy match for search suggestions
  const levenshtein = (a, b) => {
    const m = a.length, n = b.length;
    const dp = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));
    for (let i = 0; i <= m; i++) dp[i][0] = i;
    for (let j = 0; j <= n; j++) dp[0][j] = j;
    for (let i = 1; i <= m; i++)
      for (let j = 1; j <= n; j++)
        dp[i][j] = a[i-1] === b[j-1] ? dp[i-1][j-1] : 1 + Math.min(dp[i-1][j], dp[i][j-1], dp[i-1][j-1]);
    return dp[m][n];
  };

  const fuzzySearch = useCallback((query, products) => {
    if (!query || query.length < 2) return [];
    const q = query.toLowerCase();
    
    return products
      .map(product => {
        const title = (product.title_translated || product.title || '').toLowerCase();
        const words = title.split(/\s+/);
        let score = 0;
        
        // Exact substring match
        if (title.includes(q)) { score = 100; }
        else {
          // Word-level fuzzy
          const queryWords = q.split(/\s+/);
          for (const qw of queryWords) {
            if (qw.length < 2) continue;
            for (const tw of words) {
              if (tw.includes(qw) || qw.includes(tw)) { score = Math.max(score, 80); continue; }
              if (qw.length >= 3) {
                const dist = levenshtein(qw, tw);
                const tolerance = qw.length <= 5 ? 2 : 3;
                if (dist <= tolerance) {
                  const sim = ((Math.max(qw.length, tw.length) - dist) / Math.max(qw.length, tw.length)) * 75;
                  score = Math.max(score, sim);
                }
              }
            }
          }
        }
        return { product, score };
      })
      .filter(item => item.score > 50)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map(item => item.product);
  }, []);

  // Fetch all products once for suggestions
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await apiInstance.get('products/');
        const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
        setAllProducts(data);
      } catch (e) {
        console.log('Failed to fetch products for suggestions');
      }
    };
    fetchProducts();
  }, []);

  // Debounced search suggestions
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    
    if (!search.trim() || search.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    
    debounceTimer.current = setTimeout(() => {
      setSuggestionsLoading(true);
      const results = fuzzySearch(search.trim(), allProducts);
      setSuggestions(results);
      setShowSuggestions(results.length > 0);
      setSuggestionsLoading(false);
    }, 200);
    
    return () => { if (debounceTimer.current) clearTimeout(debounceTimer.current); };
  }, [search, allProducts, fuzzySearch]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target) &&
          mobileSearchRef.current && !mobileSearchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Get current language data
  const currentLangData = languages.find(l => l.code === currentLanguage) || languages[0];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await apiInstance.get('category/');
        const data = Array.isArray(response.data) ? response.data : (response.data.results || []);
        setCategories(data);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  const handleSearchSubmit = () => {
    if (search.trim()) {
      setShowSuggestions(false);
      navigate(`/search?query=${encodeURIComponent(search.trim())}`);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearchSubmit();
  };

  const navItems = [
    { path: '/', label: t('home'), icon: Home },
    { path: '/products/', label: t('products'), icon: Package },
    { path: '/about/', label: t('about'), icon: Info },
    { path: '/contact/', label: t('contact'), icon: Phone },
  ];

  const categoryLinks = [
    { label: t('offersOfTheDay'), href: '/products?promotion=true' },
    { label: t('mostViewed'), href: '/products?sort=views' },
    { label: t('vendedores'), href: '/vendedores/' },
    ...categories.slice(0, 4).map(cat => ({
      label: cat.title_translated || cat.title,
      href: `/products?category=${cat.id}`
    })),
  ];

  return (
    <header className={`sticky top-0 z-50 w-full transition-all duration-300 ${
      isScrolled 
        ? 'bg-gray-100 shadow-lg' 
        : 'bg-gray-50 shadow-md'
    }`}>
      {/* Currency Ticker - Top */}
      <CurrencyTicker />

      {/* Main Header */}
      <div className="mx-auto max-w-7xl px-4">
        <div className="flex h-20 items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img 
              src="/logomain.png" 
              alt="ASMAR SHOP" 
              className="h-52 sm:h-[18.2rem] w-auto object-contain"
            />
          </Link>

          {/* Search Bar - Hidden on mobile */}
          <div className="hidden flex-1 max-w-2xl md:flex relative" ref={searchRef}>
            <div className="flex w-full">
              <Input
                type="text"
                placeholder={t('searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                className="h-10 rounded-r-none border-0 bg-card text-foreground placeholder:text-muted-foreground focus-visible:ring-0"
              />
              <Button 
                onClick={handleSearchSubmit}
                className="h-10 rounded-l-none bg-accent text-accent-foreground hover:bg-accent/90 px-6"
              >
                <Search className="h-5 w-5" />
              </Button>
            </div>
            {/* Search Suggestions Dropdown */}
            {showSuggestions && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden max-h-[400px] overflow-y-auto">
                {suggestionsLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : (
                  suggestions.map((product) => (
                    <Link
                      key={product.id || product.pid}
                      to={`/detail/${product.slug || product.id}`}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-secondary transition-colors"
                      onClick={() => { setShowSuggestions(false); setSearch(''); }}
                    >
                      <div className="w-10 h-10 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                        <img 
                          src={product.image || '/placeholder.jpg'} 
                          alt="" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {product.title_translated || product.title}
                        </p>
                        <p className="text-xs text-accent font-semibold">
                          ${parseFloat(product.price || 0).toFixed(2)}
                        </p>
                      </div>
                    </Link>
                  ))
                )}
                <Link
                  to={`/search?query=${encodeURIComponent(search.trim())}`}
                  className="block text-center text-sm text-accent font-medium py-3 border-t border-border hover:bg-secondary transition-colors"
                  onClick={() => setShowSuggestions(false)}
                >
                  {t('viewAll')} &rarr;
                </Link>
              </div>
            )}
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 text-gray-700 hover:text-gray-900 transition-colors px-2 py-1 rounded-md hover:bg-gray-100">
                  <img 
                    src={currentLangData.flag} 
                    alt={currentLangData.country}
                    className="w-6 h-4 object-cover rounded shadow-sm"
                  />
                  <span className="text-xs font-semibold hidden md:block">
                    {currentLangData.code === 'pt-BR' ? 'BR' : 'PY'}
                  </span>
                  <ChevronDown className="h-4 w-4 hidden md:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[160px]">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    className={`flex items-center gap-3 cursor-pointer ${currentLanguage === lang.code ? 'bg-accent/10' : ''}`}
                  >
                    <img 
                      src={lang.flag} 
                      alt={lang.country}
                      className="w-6 h-4 object-cover rounded shadow-sm"
                    />
                    <span className="font-medium">{lang.code === 'pt-BR' ? 'BR' : 'PY'}</span>
                    <span className="text-muted-foreground text-sm">{lang.country}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User Menu */}
            {isLoggedIn() ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors">
                    <User className="h-5 w-5" />
                    <span className="hidden md:block text-sm">
                      {user()?.username || t('account')}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild>
                    <Link to="/customer/account/" className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {t('myAccount')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/customer/orders/" className="flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      {t('myOrders')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/customer/wishlist/" className="flex items-center gap-2">
                      <Heart className="h-4 w-4" />
                      {t('wishlist')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/customer/settings/" className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      {t('settings')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/logout/" className="flex items-center gap-2 text-destructive">
                      <LogOut className="h-4 w-4" />
                      {t('logout')}
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link 
                to="/login" 
                className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors"
              >
                <User className="h-5 w-5" />
                <span className="hidden md:block text-sm">
                  {t('login')}
                  <span className="hidden lg:block font-semibold text-xs">{t('myAccount')}</span>
                </span>
              </Link>
            )}

            {/* Cart */}
            <Link 
              to="/cart" 
              className="relative flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors"
            >
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-accent text-xs font-bold text-accent-foreground">
                  {cartCount}
                </span>
              )}
              <span className="hidden md:block text-sm">{t('cart')}</span>
            </Link>

            {/* Mobile Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="md:hidden text-gray-700 hover:bg-gray-200"
                >
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80">
                <SheetHeader>
                  <SheetTitle>Menu</SheetTitle>
                </SheetHeader>
                <nav className="mt-6 flex flex-col gap-2">
                  {navItems.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          location.pathname === item.path
                            ? 'bg-primary text-primary-foreground'
                            : 'hover:bg-secondary'
                        }`}
                      >
                        <Icon className="h-5 w-5" />
                        {item.label}
                      </Link>
                    );
                  })}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        {/* Mobile Search */}
        <div className="pb-3 md:hidden relative" ref={mobileSearchRef}>
          <div className="flex w-full">
            <Input
              type="text"
              placeholder={t('searchPlaceholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              className="h-10 rounded-r-none border-0 bg-card text-foreground placeholder:text-muted-foreground focus-visible:ring-0"
            />
            <Button 
              onClick={handleSearchSubmit}
              className="h-10 rounded-l-none bg-accent text-accent-foreground hover:bg-accent/90 px-4"
            >
              <Search className="h-5 w-5" />
            </Button>
          </div>
          {/* Mobile Search Suggestions */}
          {showSuggestions && (
            <div className="absolute top-full left-0 right-0 mt-1 mx-4 bg-card border border-border rounded-xl shadow-xl z-50 overflow-hidden max-h-[300px] overflow-y-auto">
              {suggestions.map((product) => (
                <Link
                  key={product.id || product.pid}
                  to={`/detail/${product.slug || product.id}`}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-secondary transition-colors"
                  onClick={() => { setShowSuggestions(false); setSearch(''); }}
                >
                  <div className="w-10 h-10 rounded-lg overflow-hidden bg-secondary flex-shrink-0">
                    <img src={product.image || '/placeholder.jpg'} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {product.title_translated || product.title}
                    </p>
                    <p className="text-xs text-accent font-semibold">
                      ${parseFloat(product.price || 0).toFixed(2)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Categories Bar - Mobile App Style */}
      <div className="bg-card border-t border-border">
        <div className="mx-auto max-w-7xl px-0 md:px-4">
          <nav className="flex items-center gap-1 md:gap-4 overflow-x-auto py-1.5 md:py-2 scrollbar-hide px-3 md:px-0 -mx-0 md:mx-0 scroll-smooth snap-x snap-mandatory">
            {/* Categories Dropdown - More prominent on mobile */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-1.5 text-xs md:text-sm font-medium text-foreground bg-secondary/60 hover:bg-secondary px-3 md:px-3 py-2 md:py-1.5 rounded-full whitespace-nowrap transition-all snap-start flex-shrink-0">
                  <Menu className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  <span>{t('categories')}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {categories.map((category) => (
                  <DropdownMenuItem key={category.id} asChild>
                    <Link to={`/products?category=${category.id}`}>
                      {category.title_translated || category.title}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Quick Links - Pill style for mobile */}
            {categoryLinks.slice(0, 6).map((link, index) => (
              <Link
                key={index}
                to={link.href}
                className="text-xs md:text-sm text-muted-foreground hover:text-foreground hover:bg-secondary/50 px-3 md:px-2 py-2 md:py-1 rounded-full whitespace-nowrap transition-all snap-start flex-shrink-0"
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default StoreHeader;
